import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Loader2,
  RefreshCw,
  ExternalLink,
  MessageCircle,
  X,
  Palette,
  Play,
  Square,
  Settings,
  Copy,
  Check,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { fetchAgents } from "@/services/templateService";
import { useToast } from "@/hooks/use-toast";
import MainLayout from "@/components/layout/MainLayout";
import PageHeader from "@/components/layout/PageHeader";

interface Agent {
  id: string;
  agent_name: string;
  title: string;
  public_id: string;
  is_active: boolean;
  is_public: boolean;
  welcome_message?: string;
  description?: string;
}

declare global {
  interface Window {
    hubfloDebug?: boolean;
    hubfloWidgetLoaded?: boolean;
  }
}

const ChatWidgetTester = () => {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const [isLoadingAgents, setIsLoadingAgents] = useState(true);
  const [agentError, setAgentError] = useState<string | null>(null);
  const [widgetLoaded, setWidgetLoaded] = useState(false);
  const [debugInfo, setDebugInfo] = useState<string>("Initializing...");
  const [widgetVersion, setWidgetVersion] = useState<"original" | "enhanced">(
    "enhanced",
  );
  const [isCopied, setIsCopied] = useState(false);

  // Style customization states
  const [customStyles, setCustomStyles] = useState({
    position: "bottom-right",
    bubbleSize: "60",
    brandColor: "",
    borderRadius: "50",
    showGreeting: true,
  });

  const currentScriptRef = useRef<HTMLScriptElement | null>(null);
  const debugIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const { user, organization } = useAuth();
  const { toast } = useToast();

  // Load agents on component mount
  useEffect(() => {
    loadAgents();

    // Enable debug mode
    window.hubfloDebug = true;

    // Start debug info updates
    debugIntervalRef.current = setInterval(updateDebugInfo, 2000);

    return () => {
      if (debugIntervalRef.current) {
        clearInterval(debugIntervalRef.current);
      }
      clearWidget();
    };
  }, []);

  const loadAgents = async () => {
    if (!organization?.id) {
      setAgentError(
        "No organization found. Please make sure you are logged in.",
      );
      setIsLoadingAgents(false);
      return;
    }

    setIsLoadingAgents(true);
    setAgentError(null);

    try {
      console.log("Fetching agents for organization:", organization.id);

      const { data, error } = await fetchAgents(organization.id, {
        includeConversationCounts: false,
      });

      if (error) {
        throw new Error(error.toString());
      }

      if (!data || data.length === 0) {
        setAgentError("No agents found. Please create an agent first.");
        setAgents([]);
        return;
      }

      setAgents(data as Agent[]);
      console.log("Loaded agents:", data);

      // Auto-select first agent
      if (data.length > 0 && !selectedAgent) {
        setSelectedAgent(data[0] as Agent);
      }
    } catch (error: any) {
      console.error("Error loading agents:", error);
      setAgentError(error.message || "Failed to load agents");
      toast({
        variant: "destructive",
        title: "Error loading agents",
        description: error.message || "Failed to load agents",
      });
    } finally {
      setIsLoadingAgents(false);
    }
  };

  const selectAgent = (agent: Agent) => {
    setSelectedAgent(agent);
    updateDebugInfo();
  };

  const getWidgetScript = () => {
    if (widgetVersion === "enhanced") {
      return "/embed.js";
    }
    return "/embed-original.js";
  };

  const applyCustomStyles = () => {
    if (!selectedAgent) return;

    // Remove existing custom styles
    const existingCustomStyle = document.querySelector("#custom-widget-styles");
    if (existingCustomStyle) {
      existingCustomStyle.remove();
    }

    // Apply custom CSS
    const customStyle = document.createElement("style");
    customStyle.id = "custom-widget-styles";

    const positionStyles = getPositionStyles();
    const bubbleSize = `${customStyles.bubbleSize}px`;
    const brandColor =
      customStyles.brandColor || organization?.brand_color || "#2563eb";
    const borderRadius = customStyles.borderRadius === "0" ? "12px" : "50%";

    customStyle.textContent = `
      /* Widget positioning */
      .hubflo-widget {
        ${positionStyles}
      }
      
      /* Bubble styling */
      .hubflo-bubble {
        width: ${bubbleSize} !important;
        height: ${bubbleSize} !important;
        border-radius: ${borderRadius} !important;
        background-color: ${brandColor} !important;
        box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15) !important;
      }
      
      .hubflo-bubble:hover {
        transform: scale(1.1) !important;
        box-shadow: 0 12px 35px rgba(0, 0, 0, 0.25) !important;
      }
      
      /* Header styling */
      .hubflo-header {
        background-color: ${brandColor} !important;
        color: white !important;
      }
      
      /* Send button styling */
      .hubflo-send {
        background-color: ${brandColor} !important;
      }
      
      .hubflo-send:hover {
        background-color: ${brandColor}dd !important;
      }
      
      /* Hide greeting if disabled */
      ${!customStyles.showGreeting ? ".hubflo-greeting { display: none !important; }" : ""}
    `;

    document.head.appendChild(customStyle);
  };

  const getPositionStyles = () => {
    switch (customStyles.position) {
      case "bottom-left":
        return "bottom: 24px !important; left: 24px !important; right: auto !important;";
      case "top-right":
        return "top: 24px !important; bottom: auto !important; right: 24px !important; left: auto !important;";
      case "top-left":
        return "top: 24px !important; bottom: auto !important; left: 24px !important; right: auto !important;";
      case "center-right":
        return "top: 50% !important; transform: translateY(-50%) !important; bottom: auto !important; right: 24px !important; left: auto !important;";
      default:
        return "bottom: 24px !important; right: 24px !important;";
    }
  };

  const testEmbedFileAccess = async (
    version: "original" | "enhanced" = widgetVersion,
  ) => {
    try {
      const scriptPath =
        version === "enhanced" ? "/embed.js" : "/embed-original.js";
      const response = await fetch(scriptPath);
      console.log(`${scriptPath} accessibility test:`, {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        url: response.url,
      });
      return response.ok;
    } catch (error) {
      console.error(`Error accessing widget script:`, error);
      return false;
    }
  };

  const loadWidget = async () => {
    if (!selectedAgent) {
      toast({
        variant: "destructive",
        title: "No agent selected",
        description: "Please select an agent first",
      });
      return;
    }

    console.log("Loading widget:", {
      agent: selectedAgent.agent_name,
      version: widgetVersion,
    });

    // Test if embed script is accessible
    const embedAccessible = await testEmbedFileAccess();
    if (!embedAccessible) {
      toast({
        variant: "destructive",
        title: "Widget script not accessible",
        description: `The ${getWidgetScript()} file could not be loaded. Check if it exists.`,
      });
      return;
    }

    // Clear any existing widget
    clearWidget();

    // Reset widget loaded flag
    if (window.hubfloWidgetLoaded) {
      delete window.hubfloWidgetLoaded;
    }

    // Load the widget script
    const script = document.createElement("script");
    script.src = getWidgetScript();
    script.setAttribute("data-agent-id", selectedAgent.public_id);
    script.setAttribute("data-type", "bubble");
    script.setAttribute(
      "data-welcome-message",
      selectedAgent.welcome_message || "Hi! How can I assist you?",
    );

    script.onload = () => {
      console.log("Widget script loaded successfully:", {
        agent: selectedAgent.agent_name,
        script: script.src,
        attributes: {
          agentId: script.getAttribute("data-agent-id"),
          type: script.getAttribute("data-type"),
          welcomeMessage: script.getAttribute("data-welcome-message"),
        },
      });

      setWidgetLoaded(true);

      // Apply custom styles after widget loads
      setTimeout(() => {
        applyCustomStyles();
        updateDebugInfo();

        // Check if widget elements were created
        const bubble = document.querySelector(".hubflo-bubble");
        const widget = document.querySelector(".hubflo-widget");

        console.log("Widget elements after load:", {
          bubble: !!bubble,
          widget: !!widget,
          bubbleHTML: bubble?.outerHTML.substring(0, 200) || "Not found",
        });
      }, 1000);

      toast({
        title: "Widget loaded successfully",
        description: `Chat widget for ${selectedAgent.agent_name} is now active`,
      });
    };

    script.onerror = (e) => {
      console.error("Failed to load widget script:", e);
      console.error("Script details:", {
        src: script.src,
        online: navigator.onLine,
        version: widgetVersion,
      });
      setWidgetLoaded(false);
      updateDebugInfo();
      toast({
        variant: "destructive",
        title: "Widget failed to load",
        description:
          "Failed to load the chat widget script. Check console for details.",
      });
    };

    document.head.appendChild(script);
    currentScriptRef.current = script;
  };

  const clearWidget = () => {
    // Remove script
    if (currentScriptRef.current && currentScriptRef.current.parentNode) {
      currentScriptRef.current.parentNode.removeChild(currentScriptRef.current);
      currentScriptRef.current = null;
    }

    // Remove widget container (both possible class names)
    const containers = document.querySelectorAll(
      ".hubflo-widget, .ai-chat-widget-container",
    );
    containers.forEach((container) => {
      if (container && container.parentNode) {
        container.parentNode.removeChild(container);
      }
    });

    // Remove custom styles
    const existingCustomStyle = document.querySelector("#custom-widget-styles");
    if (existingCustomStyle) {
      existingCustomStyle.remove();
    }

    // Reset widget loaded flag
    if (window.hubfloWidgetLoaded) {
      delete window.hubfloWidgetLoaded;
    }

    setWidgetLoaded(false);
    console.log("Widget cleared");
    updateDebugInfo();

    toast({
      title: "Widget cleared",
      description: "Chat widget has been removed",
    });
  };

  const updateDebugInfo = () => {
    const hubfloContainer = document.querySelector(".hubflo-widget");
    const hubfloBubble = document.querySelector(".hubflo-bubble");
    const hubfloWindow = document.querySelector(".hubflo-window");

    // Legacy selectors
    const aiContainer = document.querySelector(".ai-chat-widget-container");
    const aiBubble = document.querySelector(".ai-chat-widget-bubble");

    const allScripts = Array.from(
      document.querySelectorAll('script[src*="embed"]'),
    );
    const widgetScript = document.querySelector("script[data-agent-id]");

    const info = `
🌐 Server: ${window.location.origin}
📅 Timestamp: ${new Date().toLocaleTimeString()}
🎯 Selected Agent: ${selectedAgent ? `${selectedAgent.agent_name} (${selectedAgent.public_id})` : "None"}
🔧 Widget Version: ${widgetVersion}

🛠️ SCRIPT STATUS:
📜 Script Loaded: ${currentScriptRef.current ? "✅ Yes" : "❌ No"}
📍 Script Src: ${currentScriptRef.current?.src || "None"}
🔍 Embed Scripts Found: ${allScripts.length}
🎯 Widget Script Data: ${widgetScript ? `✅ Agent ID: ${widgetScript.getAttribute("data-agent-id")}` : "❌ Not Found"}
🏴 Widget Loaded Flag: ${window.hubfloWidgetLoaded ? "✅ True" : "❌ False"}

🖼️ WIDGET STATUS (Hubflo):
📦 Widget Container: ${hubfloContainer ? "✅ Found" : "❌ Not Found"}
🎈 Widget Bubble: ${hubfloBubble ? "✅ Visible" : "❌ Not Visible"}
🪟 Widget Window: ${hubfloWindow ? "✅ Present" : "❌ Not Present"}

🖼️ LEGACY STATUS (AI Chat):
📦 Legacy Container: ${aiContainer ? "✅ Found" : "❌ Not Found"}
🎈 Legacy Bubble: ${aiBubble ? "✅ Visible" : "❌ Not Visible"}

📊 ENVIRONMENT:
👥 Total Agents: ${agents.length}
🔍 Debug Mode: ${window.hubfloDebug ? "✅ Enabled" : "❌ Disabled"}
🏢 Organization: ${organization?.name || "None"}
👤 User: ${user?.email || "None"}
🎨 Brand Color: ${organization?.brand_color || "Default"}
📍 Position: ${customStyles.position}
📏 Size: ${customStyles.bubbleSize}px

💡 TESTING TIPS:
- Use "Load Widget" to test specific agents
- Switch between Original/Enhanced versions
- Check browser console for detailed logs
- Verify widget script accessibility first
        `.trim();

    setDebugInfo(info);
  };

  const copyDebugInfo = async () => {
    try {
      await navigator.clipboard.writeText(debugInfo);
      setIsCopied(true);
      toast({
        title: "Debug info copied!",
        description: "Debug information has been copied to your clipboard.",
      });

      // Reset copy state after 2 seconds
      setTimeout(() => {
        setIsCopied(false);
      }, 2000);
    } catch (error) {
      console.error("Failed to copy debug info:", error);
      toast({
        variant: "destructive",
        title: "Copy failed",
        description: "Failed to copy debug information to clipboard.",
      });
    }
  };

  return (
    <MainLayout>
      <div className="container mx-auto p-6 space-y-6 max-h-screen overflow-y-auto">
        <PageHeader
          title="Chat Widget Tester"
          description="Test your chat widgets by selecting an agent and widget version"
        />

        {/* Widget Version Selection */}
        <Card className="border-2 border-primary/20 bg-primary/5">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Widget Version Selection
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4 mb-4">
              <Label htmlFor="widget-version">Widget Version:</Label>
              <select
                id="widget-version"
                className="p-2 border rounded-md"
                value={widgetVersion}
                onChange={(e) =>
                  setWidgetVersion(e.target.value as "original" | "enhanced")
                }
              >
                <option value="enhanced">
                  Enhanced (/embed.js) - Desktop Layout Match
                </option>
                <option value="original">
                  Original (/embed-original.js) - Basic
                </option>
              </select>
            </div>

            <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
              <h4 className="font-semibold text-blue-800 mb-2">
                ✨ Enhanced Widget Features:
              </h4>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>
                  • 🖥️ Desktop layout dimensions (45vw width, 90vh height)
                </li>
                <li>• 🖼️ Zero-spacing banner image display</li>
                <li>• 🎨 Dynamic brand color theming</li>
                <li>• 📱 Responsive mobile/desktop behavior</li>
                <li>• 🔄 Real organization data integration</li>
                <li>• 💬 Enhanced message styling</li>
              </ul>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Button
                onClick={loadWidget}
                disabled={isLoadingAgents || !selectedAgent}
                className="flex items-center gap-2"
              >
                <Play className="h-4 w-4" />
                Load Widget
              </Button>

              <Button
                variant="outline"
                onClick={clearWidget}
                disabled={!widgetLoaded}
                className="flex items-center gap-2"
              >
                <Square className="h-4 w-4" />
                Clear Widget
              </Button>

              <Button
                variant="outline"
                onClick={async () => {
                  const accessible = await testEmbedFileAccess();
                  toast({
                    variant: accessible ? "default" : "destructive",
                    title: accessible
                      ? "Widget script accessible"
                      : "Widget script not accessible",
                    description: accessible
                      ? `The ${getWidgetScript()} script can be loaded`
                      : `The ${getWidgetScript()} script cannot be accessed`,
                  });
                }}
                className="flex items-center gap-2"
              >
                <RefreshCw className="h-4 w-4" />
                Test Script Access
              </Button>
            </div>

            {widgetVersion === "enhanced" && (
              <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-700">
                  <strong>Enhanced Version:</strong> Built from React components
                  with zero spacing between header and banner, improved styling,
                  and better event handling.
                </p>
                <p className="text-xs text-blue-600 mt-1">
                  💡 To update: Edit components in{" "}
                  <code>src/components/widget/</code> then run{" "}
                  <code>npm run build:widget</code>
                </p>
              </div>
            )}

            {widgetVersion === "original" && (
              <div className="mt-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <p className="text-sm text-gray-700">
                  <strong>Original Version:</strong> The standard widget
                  implementation for comparison.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Style Customization */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Palette className="h-5 w-5" />
              Widget Style Customization
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  window.open("/settings?tab=appearance", "_blank")
                }
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Open Appearance Settings
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="position">Position</Label>
                <select
                  id="position"
                  className="w-full p-2 border rounded-md"
                  value={customStyles.position}
                  onChange={(e) =>
                    setCustomStyles((prev) => ({
                      ...prev,
                      position: e.target.value,
                    }))
                  }
                >
                  <option value="bottom-right">Bottom Right</option>
                  <option value="bottom-left">Bottom Left</option>
                  <option value="top-right">Top Right</option>
                  <option value="top-left">Top Left</option>
                  <option value="center-right">Center Right</option>
                </select>
              </div>

              <div>
                <Label htmlFor="bubbleSize">Bubble Size (px)</Label>
                <Input
                  id="bubbleSize"
                  type="number"
                  min="40"
                  max="120"
                  value={customStyles.bubbleSize}
                  onChange={(e) =>
                    setCustomStyles((prev) => ({
                      ...prev,
                      bubbleSize: e.target.value,
                    }))
                  }
                />
              </div>

              <div>
                <Label htmlFor="brandColor">Custom Brand Color</Label>
                <Input
                  id="brandColor"
                  type="color"
                  value={
                    customStyles.brandColor ||
                    organization?.brand_color ||
                    "#2563eb"
                  }
                  onChange={(e) =>
                    setCustomStyles((prev) => ({
                      ...prev,
                      brandColor: e.target.value,
                    }))
                  }
                />
              </div>

              <div>
                <Label htmlFor="borderRadius">Shape</Label>
                <select
                  id="borderRadius"
                  className="w-full p-2 border rounded-md"
                  value={customStyles.borderRadius}
                  onChange={(e) =>
                    setCustomStyles((prev) => ({
                      ...prev,
                      borderRadius: e.target.value,
                    }))
                  }
                >
                  <option value="50">Circle</option>
                  <option value="0">Rounded Square</option>
                </select>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="showGreeting"
                  checked={customStyles.showGreeting}
                  onChange={(e) =>
                    setCustomStyles((prev) => ({
                      ...prev,
                      showGreeting: e.target.checked,
                    }))
                  }
                />
                <Label htmlFor="showGreeting">Show Greeting Bubble</Label>
              </div>
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={applyCustomStyles}
                disabled={!widgetLoaded}
              >
                Apply Custom Styles
              </Button>
              <Button
                onClick={loadWidget}
                disabled={isLoadingAgents || !selectedAgent}
                className="flex items-center gap-2"
              >
                <RefreshCw className="h-4 w-4" />
                Refresh Widget
              </Button>
              <Button
                variant="outline"
                onClick={() =>
                  setCustomStyles({
                    position: "bottom-right",
                    bubbleSize: "60",
                    brandColor: "",
                    borderRadius: "50",
                    showGreeting: true,
                  })
                }
              >
                Reset to Default
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Agent Selection */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageCircle className="h-5 w-5" />
              Available Agents
              <Button
                variant="outline"
                size="sm"
                onClick={loadAgents}
                disabled={isLoadingAgents}
              >
                <RefreshCw
                  className={`h-4 w-4 ${isLoadingAgents ? "animate-spin" : ""}`}
                />
                Refresh
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoadingAgents ? (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading agents...
              </div>
            ) : agentError ? (
              <Alert variant="destructive">
                <AlertDescription>{agentError}</AlertDescription>
              </Alert>
            ) : agents.length === 0 ? (
              <Alert>
                <AlertDescription>
                  No agents found. Please create an agent first.
                </AlertDescription>
              </Alert>
            ) : (
              <div className="space-y-3">
                {agents.map((agent) => (
                  <div
                    key={agent.id}
                    className={`p-4 border rounded-lg cursor-pointer transition-all hover:bg-muted/50 ${
                      selectedAgent?.id === agent.id
                        ? "border-primary bg-primary/5"
                        : "border-border"
                    }`}
                    onClick={() => selectAgent(agent)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-medium">
                            {agent.agent_name || agent.title}
                          </h3>
                          <div className="flex gap-1">
                            <Badge
                              variant={
                                agent.is_active ? "default" : "secondary"
                              }
                            >
                              {agent.is_active ? "Active" : "Inactive"}
                            </Badge>
                            <Badge
                              variant={agent.is_public ? "default" : "outline"}
                            >
                              {agent.is_public ? "Public" : "Private"}
                            </Badge>
                          </div>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          ID: {agent.public_id}
                        </p>
                        {agent.description && (
                          <p className="text-sm text-muted-foreground">
                            {agent.description}
                          </p>
                        )}
                        {agent.welcome_message && (
                          <p className="text-sm italic text-muted-foreground">
                            "{agent.welcome_message}"
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Test Controls */}
        {selectedAgent && (
          <Card>
            <CardHeader>
              <CardTitle>Test Controls</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-3">
                <Button
                  onClick={loadWidget}
                  disabled={!selectedAgent}
                  className="flex items-center gap-2"
                >
                  <MessageCircle className="h-4 w-4" />
                  Load Widget on This Page
                </Button>

                <Button
                  variant="outline"
                  onClick={clearWidget}
                  disabled={!widgetLoaded}
                  className="flex items-center gap-2"
                >
                  <X className="h-4 w-4" />
                  Clear Widget
                </Button>

                <Button
                  variant="outline"
                  onClick={async () => {
                    const accessible = await testEmbedFileAccess();
                    toast({
                      variant: accessible ? "default" : "destructive",
                      title: accessible
                        ? "Embed.js accessible"
                        : "Embed.js not accessible",
                      description: accessible
                        ? "The embed script can be loaded"
                        : "The embed script cannot be accessed",
                    });
                  }}
                  className="flex items-center gap-2"
                >
                  <RefreshCw className="h-4 w-4" />
                  Test Embed Script
                </Button>

                <Button
                  variant="outline"
                  onClick={() => {
                    if (!selectedAgent || !organization?.agent_url) return;
                    const url = `${window.location.origin}/${encodeURIComponent(organization.agent_url)}/${selectedAgent.public_id}`;
                    window.open(url, "_blank");
                  }}
                  disabled={!selectedAgent}
                  className="flex items-center gap-2"
                >
                  <ExternalLink className="h-4 w-4" />
                  Open Chat Page
                </Button>

                <Button
                  variant="outline"
                  onClick={() => {
                    if (!selectedAgent) return;
                    const url = `${window.location.origin}/preview-bubble/${selectedAgent.public_id}`;
                    window.open(url, "_blank");
                  }}
                  disabled={!selectedAgent}
                  className="flex items-center gap-2"
                >
                  <ExternalLink className="h-4 w-4" />
                  Open Preview Page
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Debug Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              Debug Information
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={copyDebugInfo}
                  className="flex items-center gap-2"
                >
                  {isCopied ? (
                    <>
                      <Check className="h-4 w-4" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="h-4 w-4" />
                      Copy
                    </>
                  )}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={updateDebugInfo}
                  className="flex items-center gap-2"
                >
                  <RefreshCw className="h-4 w-4" />
                  Refresh
                </Button>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <pre className="text-sm font-mono bg-muted p-4 rounded-lg whitespace-pre-wrap">
              {debugInfo}
            </pre>
          </CardContent>
        </Card>

        {/* Test Area */}
        <Card>
          <CardHeader>
            <CardTitle>Widget Test Area</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="min-h-[400px] bg-muted/20 rounded-lg p-6 flex items-center justify-center">
              <div className="text-center space-y-4">
                <h3 className="text-lg font-medium">Widget Testing Zone</h3>
                <p className="text-muted-foreground max-w-md">
                  Select an agent and widget version above, then click "Load
                  Widget" to test. The chat bubble will appear according to your
                  style settings.
                </p>
                {selectedAgent ? (
                  <div className="text-sm text-muted-foreground">
                    Ready to test: <strong>{selectedAgent.agent_name}</strong>
                    <br />
                    Widget Version:{" "}
                    <strong>
                      {widgetVersion === "enhanced"
                        ? "Enhanced (Zero Spacing)"
                        : "Original"}
                    </strong>
                  </div>
                ) : agents.length > 0 ? (
                  <div className="text-sm text-muted-foreground">
                    Select an agent above to begin testing
                  </div>
                ) : (
                  <div className="text-sm text-muted-foreground">
                    Create an agent first to begin testing
                  </div>
                )}

                <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <h4 className="font-medium text-yellow-800">
                    💡 Testing Tips:
                  </h4>
                  <ul className="text-sm text-yellow-700 mt-2 space-y-1 text-left">
                    <li>
                      • <strong>Enhanced Version:</strong> Features zero spacing
                      between header and banner
                    </li>
                    <li>
                      • <strong>Original Version:</strong> Standard
                      implementation for comparison
                    </li>
                    <li>
                      • Use the style controls above to customize the widget
                      appearance
                    </li>
                    <li>• Click "Apply Custom Styles" after making changes</li>
                    <li>
                      • Visit <strong>Settings → Appearance</strong> for
                      permanent brand settings
                    </li>
                    <li>
                      • Check browser console for detailed debug information
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
};

export default ChatWidgetTester;
